// frontend/src/services/payments.js

import api from "./api";

/**
 * Initiate payment for a self-serve plan ("solo" | "professional").
 * @param {string} planType - The chosen plan. Defaults to "professional".
 * @param {string|null} winbackToken - Optional one-time first-month win-back
 *   discount token (from the post-trial comeback email). Validated server-side;
 *   ignored if invalid/expired/already-redeemed.
 * @returns {Promise<Object>} Response containing payment_url and transaction_id
 */
export const initiatePayment = async (planType = "professional", winbackToken = null) => {
  const body = { plan_type: planType };
  if (winbackToken) body.winback_token = winbackToken;
  const response = await api.post("/payments/initiate", body);
  return response.data;
};

/**
 * Read the current upgrade offer for the signed-in user.
 *
 * GET /payments/offer is a PREVIEW of exactly what `initiatePayment` would
 * charge - both go through the same server-side resolver, so display == charge
 * by construction. Nothing here may recompute a price: percents are fractional
 * (1.5% x whole trial days left) and the final price is floored to a whole
 * shekel server-side.
 *
 * Passing the win-back token does NOT consume it - the token is only redeemed
 * by a completed payment - so this is safe to call on every Payment mount.
 *
 * An INELIGIBLE user is a normal 200 carrying `eligible: false`, never an
 * error; callers treat a thrown error and `eligible: false` the same way
 * (render no offer), which is what keeps every selling surface fail-closed.
 *
 * @param {string|null} winbackToken - Optional token from the comeback email.
 * @returns {Promise<Object>} `{ eligible, discount_kind, discount_percent,
 *   days_left, offer_ends_at, trial_ends_at, server_now, plans, has_hot_opportunity }`
 */
export const getUpgradeOffer = async (winbackToken = null) => {
  const params = {};
  if (winbackToken) params.winback_token = winbackToken;
  const response = await api.get("/payments/offer", { params });
  return response.data;
};

/**
 * Get user's payment transaction history
 * @param {number} limit - Number of transactions to return (default 50)
 * @param {number} skip - Pagination offset (default 0)
 * @returns {Promise<Object>} Transaction list with pagination metadata
 */
export const getUserTransactions = async (limit = 50, skip = 0) => {
  const response = await api.get("/payments/transactions", {
    params: { limit, skip }
  });
  return response.data;
};

/**
 * Cancel user's subscription (effective at period end)
 * @returns {Promise<Object>} Cancellation confirmation with subscription details
 */
export const cancelSubscription = async () => {
  const response = await api.post("/payments/subscriptions/cancel");
  return response.data;
};

/**
 * Reactivate a subscription that was cancelled but is still within the paid period.
 *
 * Calls POST /payments/subscriptions/reactivate. With token-based recurring
 * billing, reactivation is just a flag flip — the backend clears
 * cancel_at_period_end / cancelled_at and the hourly billing job will charge
 * the saved token at the next period_end as usual. Only valid when the
 * subscription still has access remaining (current_period_end in the future);
 * otherwise the caller should start a fresh subscription via initiatePayment().
 *
 * @returns {Promise<Object>} Reactivation confirmation with updated subscription
 */
export const reactivateSubscription = async () => {
  const response = await api.post("/payments/subscriptions/reactivate");
  return response.data;
};

// ---------------------------------------------------------------------------
// #349 - Enterprise billing: the public pay link, card update, and the
// customer's own Enterprise billing summary.
// ---------------------------------------------------------------------------

/**
 * The ONLY hosts a HYP pay-page redirect may point at.
 *
 * Mirrors the backend's live-HYP host list (`hyp_client._REAL_HYP_HOSTS`). The
 * backend signs the URL, so a different host means something is wrong - a
 * misconfigured environment (dev points HYP at `hyp.invalid.test`) or a
 * tampered response - and the customer must never be sent to a payment page we
 * did not expect. Adding a host here is a deliberate change in BOTH repos'
 * sense of "where HYP lives", never a convenience.
 */
export const HYP_REDIRECT_HOSTS = Object.freeze(["pay.hyp.co.il", "icom.yaad.net"]);

/**
 * True only for an `https:` URL whose host is exactly one of
 * `HYP_REDIRECT_HOSTS` (no user-info, no port, no look-alike subdomain).
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isHypRedirectUrl = (value) => {
  if (typeof value !== "string" || value.length === 0) return false;
  let url;
  try {
    url = new URL(value);
  } catch (error) {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (url.username || url.password || url.port) return false;
  return HYP_REDIRECT_HOSTS.includes(url.hostname.toLowerCase());
};

/**
 * Raised by the two public pay-link calls. Carries the HTTP status and the
 * backend's refusal `reason` token only - NEVER the request body, which holds
 * the customer's pay-link token.
 */
export class EnterprisePayError extends Error {
  constructor(status, reason) {
    super("Enterprise pay request failed");
    this.name = "EnterprisePayError";
    this.status = status;
    this.reason = reason || null;
  }
}

/**
 * One call to a PUBLIC pay-link route. The token travels in the JSON BODY,
 * never in the URL (#349 R3-04): a URL path is written to Cloud Run's and the
 * load balancer's own request logs, which the app's log redaction cannot
 * reach, and the token is the page's whole authorization for 14 days.
 *
 * DELIBERATELY A BARE `fetch`, NOT the shared axios instance: that instance's
 * response interceptor logs request details on a network error, a 429 and
 * every 5xx, and would try a session refresh on a 401 that this anonymous
 * route never needs. `credentials: "omit"` - the route is anonymous and
 * nothing about the visitor's session belongs on it. `referrerPolicy:
 * "no-referrer"` - the page that makes the call is a pay-link page, and its
 * address must not ride along as a `Referer` header into those same logs. The
 * base URL is the axios instance's own, so both point at one API origin.
 *
 * @param {"summary"|"start"} route
 * @param {Object} body the JSON body, `token` included.
 * @returns {Promise<Object>}
 */
const callEnterprisePay = async (route, body) => {
  const base = (api.defaults && api.defaults.baseURL) || "";
  let response;
  try {
    response = await fetch(`${base}/enterprise-pay/${route}`, {
      method: "POST",
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (error) {
    // A network failure: status 0, and nothing from the request in the error.
    throw new EnterprisePayError(0, null);
  }
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }
  if (!response.ok) {
    const reason = data && data.detail && typeof data.detail.reason === "string"
      ? data.detail.reason
      : null;
    throw new EnterprisePayError(response.status, reason);
  }
  return data;
};

/**
 * The landing summary behind an emailed Enterprise pay link.
 *
 * POST /enterprise-pay/summary `{token}` -> `{kind: "pilot"|"card_setup",
 * business_name, amount|null, pilot_end_date|null, consent_version,
 * saved_card}`. Any link that is not live (unknown, expired, used, cancelled)
 * is ONE generic 404. `saved_card` is `{expiry: "MM/YY"}` only on a card-setup
 * link of a customer who already pays a Solo/Pro subscription with a saved
 * card, else null.
 *
 * @param {string} token
 * @returns {Promise<Object>}
 * @throws {EnterprisePayError}
 */
export const getEnterprisePayLink = (token) =>
  callEnterprisePay("summary", { token: String(token || "") });

/**
 * Record the customer's consent and either get the HYP pay-page URL or, on a
 * card-setup link, keep the card the customer's subscription already holds.
 *
 * POST /enterprise-pay/start `{token, consent: true, use_saved_card,
 * business_name?}` -> `{redirect_url: string|null, completed: boolean}`.
 * `completed: true` means the saved card was used and nothing is left to do at
 * HYP; otherwise the caller must still check `isHypRedirectUrl` before
 * navigating.
 *
 * `businessName` answers the summary's `business_name_missing` (HYP page
 * only, never with the saved card): the backend saves it to the account when
 * it still has no business name and pre-fills HYP's page with it. A blank
 * value is not sent at all.
 *
 * @param {string} token
 * @param {{useSavedCard?: boolean, businessName?: string}} [options]
 * @returns {Promise<{redirect_url: string|null, completed: boolean}>}
 * @throws {EnterprisePayError}
 */
export const startEnterprisePayment = (token, { useSavedCard = false, businessName } = {}) => {
  const body = {
    token: String(token || ""),
    consent: true,
    use_saved_card: useSavedCard === true,
  };
  const name = typeof businessName === "string" ? businessName.trim() : "";
  if (name && body.use_saved_card === false) body.business_name = name;
  return callEnterprisePay("start", body);
};

/**
 * Start a HYP card check (nothing is charged) that replaces the signed-in
 * customer's card on file - Solo/Pro subscription holder or Enterprise
 * billing-account holder.
 *
 * POST /payments/card-update -> `{redirect_url}`. HYP returns the customer to
 * `/settings?tab=billing&card=updated|failed`.
 *
 * @returns {Promise<{redirect_url: string}>}
 */
export const requestCardUpdate = async () => {
  const response = await api.post("/payments/card-update");
  return response.data;
};

/**
 * Pay the pilot / save the card for the signed-in customer's OWN Enterprise
 * account that is awaiting payment - the Billing tab's in-tab equivalent of
 * the emailed pay link (no token: the session is the authorization).
 *
 * POST /payments/enterprise/start `{consent: true, use_saved_card}` ->
 * `{redirect_url: string|null, completed: boolean}`. `completed: true` means a
 * card-setup account kept its saved card and nothing is left to do at HYP;
 * otherwise the caller must still check `isHypRedirectUrl` before navigating.
 * HYP returns the customer to `/settings?tab=billing&pay=success|failure|processing`.
 * A refusal answers `detail: {reason, message}`.
 *
 * @param {{useSavedCard?: boolean}} [options]
 * @returns {Promise<{redirect_url: string|null, completed: boolean}>}
 */
export const startOwnEnterprisePayment = async ({ useSavedCard = false } = {}) => {
  const response = await api.post("/payments/enterprise/start", {
    consent: true,
    use_saved_card: useSavedCard === true,
  });
  return response.data;
};

/**
 * The signed-in customer's OWN Enterprise billing summary.
 *
 * GET /payments/enterprise/summary -> `{has_account: false}` when there is no
 * billing account, else `{has_account: true, status, pilot_end_date,
 * card_last4, card_expiry (MM/YY), next_charge_date, service_ends_on,
 * invoices: [...]}`. An invoice whose outcome the owner is still confirming
 * arrives as `pending`. `service_ends_on` (a cancelled account only, may be
 * absent) is the last day its service runs. An `awaiting_payment` account also
 * carries `pay_kind` ("pilot"|"card_setup"|null), `pay_amount` (string|null)
 * and `saved_card_expiry` ("MM/YY"|null) for the in-tab payment, and
 * `pay_blocked_reason` ("pilot_end_date_past"|"subscription_charge_unresolved"
 * |null) when it cannot be paid right now.
 *
 * @returns {Promise<Object>}
 */
export const getEnterpriseBillingSummary = async () => {
  const response = await api.get("/payments/enterprise/summary");
  return response.data;
};
